import type { PlatformServices } from "./PlatformServices";

/** The connectivity of the device, shared by every component that asks for it (F-78). */
export interface OnlineStatusStore {
  /**
   * Subscribes to changes; the first subscriber starts listening to the platform and asks it once
   * for the current connectivity, the last one stops listening.
   *
   * @param listener - Called after every change.
   * @returns A function that ends the subscription.
   */
  readonly subscribe: (listener: () => void) => () => void;
  /**
   * Returns the last known connectivity.
   *
   * @returns `true` if online.
   */
  readonly getSnapshot: () => boolean;
}

/**
 * Creates the shared connectivity of the device (browser events in the PWA, the `Network` plugin in
 * the native apps, T-082). It starts with what the engine reports synchronously
 * (`navigator.onLine`), so a page opened without a connection never shows „online“ first, and
 * follows the platform from the first subscriber on. All components read one value, so they never
 * disagree for a moment.
 *
 * @param platform - The platform services.
 * @param initial - The connectivity known before the platform answers.
 * @returns The store.
 */
export function createOnlineStatusStore(
  platform: PlatformServices,
  initial: boolean,
): OnlineStatusStore {
  let online = initial;
  let stopPlatform: (() => void) | null = null;
  const listeners = new Set<() => void>();

  const apply = (value: boolean): void => {
    if (value === online) {
      return;
    }

    online = value;

    for (const listener of [...listeners]) {
      listener();
    }
  };

  const subscribe = (listener: () => void): (() => void) => {
    listeners.add(listener);

    if (stopPlatform === null) {
      const unsubscribe = platform.onNetworkChange(apply);
      let active = true;
      stopPlatform = (): void => {
        active = false;
        unsubscribe();
      };
      platform.isOnline().then(
        (value) => {
          if (active) {
            apply(value);
          }
        },
        () => undefined,
      );
    }

    const unsubscribeListener = (): void => {
      listeners.delete(listener);

      if (listeners.size === 0 && stopPlatform !== null) {
        stopPlatform();
        stopPlatform = null;
      }
    };

    return unsubscribeListener;
  };

  const store: OnlineStatusStore = {
    subscribe,
    getSnapshot: () => online,
  };

  return store;
}

/**
 * Returns what the engine reports about the connectivity right now, `true` where it reports nothing.
 *
 * @returns `navigator.onLine`, or `true` without a navigator.
 */
export function engineOnline(): boolean {
  const navigatorValue: unknown = Reflect.get(globalThis, "navigator");
  const online =
    typeof navigatorValue === "object" && navigatorValue !== null && "onLine" in navigatorValue
      ? navigatorValue.onLine !== false
      : true;

  return online;
}
